"use client";

import {
  Dropdown,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconClear,
  IconSearch,
  ListingsSearch,
  MobileAuth,
  MobileProfileForm,
} from "@/components";
import AuthDialog from "@/components/AuthDialog/AuthDialog";
import ProfileDialog from "@/components/ProfileDialog/ProfileDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useDialog } from "@/contexts/DialogContext";
import { useAnalytics } from "@/hooks/useAnalytics";
import { FORM_TYPES } from "@/utils";
import Image from "next/image";
import Link from "next/link";
import React, { forwardRef, useEffect, useState } from "react";

interface HeaderProps {
  ref?: React.Ref<HTMLElement>;
}

const Header: React.FC<HeaderProps> = forwardRef<HTMLElement, HeaderProps>((props, ref) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [currentView, setCurrentView] = useState<
    | "main"
    | "auth"
    | "wishlist"
    | "bag-concierge"
    | "language"
    | "currency"
    | "profile"
  >("main");
  const [langCurrencyDropdownOpen, setLangCurrencyDropdownOpen] = useState(false);
  const [authView, setAuthView] = useState<typeof FORM_TYPES[keyof typeof FORM_TYPES] | null>(null);

  const menuBtnRef = React.useRef<HTMLButtonElement>(null);
  const searchBtnRef = React.useRef<HTMLButtonElement>(null);
  const langCurrencyBtnRef = React.useRef<HTMLButtonElement>(null);

  const { isAuthenticated, isLoading, user } = useAuth();
  const { openDialog } = useDialog();
  const { selectedCurrency, setSelectedCurrency, availableCurrencies } = useCurrency();
  const { logEvent } = useAnalytics();

  const handleCurrencySelect = (currencyCode: string) => {
    const oldCurrency = selectedCurrency;
    setSelectedCurrency(currencyCode);
    setLangCurrencyDropdownOpen(false);
    setMenuOpen(false);

    logEvent("currency_changed", {
      old_currency: oldCurrency,
      new_currency: currencyCode,
      context: "header_dropdown"
    });
  };

  const getCurrencySymbol = (code: string) => {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      AUD: 'A$',
      CAD: 'CA$',
      CHF: 'CHF',
      HKD: 'HK$',
      CNY: 'CN¥',
    };
    return symbols[code] || code;
  };

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [menuOpen]);

  const handleLoginClick = () => {
    logEvent("auth_modal_opened", {
      initial_mode: "login",
      context: "header_login_button",
      user_authenticated: isAuthenticated
    });

    openDialog({
      component: AuthDialog,
      props: { initialMode: FORM_TYPES.LOGIN },
      options: {
        showCloseButton: true,
        closeOnOverlayClick: true,
      },
    });
  };

  const handleProfileClick = () => {
    logEvent("profile_modal_opened", {
      context: "header_profile_button",
      user_name: user?.first_name || "unknown"
    });

    openDialog({
      component: ProfileDialog,
      props: {},
      options: {
        showCloseButton: true,
        closeOnOverlayClick: true,
      },
    });
  };

  const handleMobileMenuClose = () => {
    setMenuOpen(false);
    setCurrentView("main");
  };

  const handleNavigateToView = (
    view: "main" | "auth" | "wishlist" | "bag-concierge" | "currency" | "profile"
  ) => {
    logEvent("mobile_menu_navigation", {
      from_view: currentView,
      to_view: view,
      context: "mobile_header_menu"
    });

    setCurrentView(view);
  };

  const handleBackToMain = () => {
    setCurrentView("main");
  };

  const handleMenuToggle = () => {
    const newMenuState = !menuOpen;
    setMenuOpen(newMenuState);

    logEvent("mobile_menu_toggle", {
      action: newMenuState ? "open" : "close",
      context: "header_hamburger_button"
    });
  };

  const handleSearchToggle = () => {
    const newSearchState = !searchOpen;
    setSearchOpen(newSearchState);

    logEvent("search_toggle", {
      action: newSearchState ? "open" : "close",
      context: "header_search_button"
    });
  };

  const renderMobileNavigationContent = () => {
    switch (currentView) {
      case "auth":
        return (
          <div className="w-full h-full bg-white flex flex-col">
            <div className="flex items-center justify-between px-4 py-6">
              <button onClick={handleBackToMain} className="text-2xl font-light">
                <IconChevronLeft width={12} height={20} />
              </button>
              <button onClick={handleMobileMenuClose} className="text-xl">
                <IconClear width={15} height={15} />
              </button>
            </div>
            <div className="flex-1 px-4 flex flex-col">
              <div className="flex border-b border-light-grey mb-6 font-raleway text-md">
                <button
                  className={`px-2 pb-3 text-center ${authView === FORM_TYPES.LOGIN ? "border-b-2 border-black" : "text-gray-400"}`}
                  onClick={() => setAuthView(FORM_TYPES.LOGIN)}
                >
                  Login
                </button>
                <button
                  className={`px-2 pb-3 text-center ${authView === FORM_TYPES.REGISTER ? "border-b-2 border-black" : "text-gray-400"}`}
                  onClick={() => setAuthView(FORM_TYPES.REGISTER)}
                >
                  Register
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <MobileAuth
                  initialMode={authView || FORM_TYPES.LOGIN}
                  onSuccess={handleMobileMenuClose}
                  onModeChange={(mode) => {
                    if (mode === FORM_TYPES.LOGIN || mode === FORM_TYPES.REGISTER) {
                      setAuthView(mode);
                    }
                  }}
                  showTabs={false}
                />
              </div>
            </div>
          </div>
        );

      case "wishlist":
        return (
          <div className="w-full h-full bg-white flex flex-col">
            <div className="flex items-center justify-between px-4 py-6">
              <button onClick={handleBackToMain} className="text-2xl font-light">
                <IconChevronLeft width={12} height={20} />
              </button>
              <button onClick={handleMobileMenuClose} className="text-xl font-light">
                <IconClear width={15} height={15} />
              </button>
            </div>
            <div className="px-4 border-b border-light-grey w-full">
              <Link
                href="/wishlist"
                onClick={handleMobileMenuClose}
                className="text-lg border-b-2 border-black inline-block"
              >
                Wishlist
              </Link>
            </div>
          </div>
        );

      case "bag-concierge":
        return (
          <div className="w-full h-full bg-white flex flex-col">
            <div className="flex items-center justify-between px-4 py-6">
              <button onClick={handleBackToMain} className="text-2xl font-light">
                <IconChevronLeft width={12} height={20} />
              </button>
              <button onClick={handleMobileMenuClose} className="text-xl font-light">
                <IconClear width={15} height={15} />
              </button>
            </div>
          </div>
        );

      case "currency":
        return (
          <div className="w-full h-full bg-white flex flex-col">
            <div className="flex items-center justify-between px-4 py-6">
              <button onClick={handleBackToMain} className="text-2xl font-light">
                <IconChevronLeft width={12} height={20} />
              </button>
              <button onClick={handleMobileMenuClose} className="text-xl font-light">
                <IconClear width={15} height={15} />
              </button>
            </div>
            <div className="flex-1 px-4 flex flex-col">
              <div className="flex border-b border-light-grey mb-6 font-raleway text-md">
                <button className="px-2 pb-3 text-center border-b-2 border-black">
                  Currency
                </button>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Choose the currency you want to see while browsing:
              </p>
              <div className="flex-1 overflow-y-auto">
                <div className="space-y-0">
                  {[
                    { label: "US Dollar", code: "USD", symbol: "$" },
                    { label: "Australian Dollar", code: "AUD", symbol: "A$" },
                    { label: "Canadian Dollar", code: "CAD", symbol: "C$" },
                    { label: "British Pound", code: "GBP", symbol: "£" },
                    { label: "Euro", code: "EUR", symbol: "€" },
                    { label: "Swiss Franc", code: "CHF", symbol: "CHF" },
                    { label: "Hong Kong Dollar", code: "HKD", symbol: "HK$" },
                    { label: "Chinese Yuan", code: "CNY", symbol: "CN¥" },
                    { label: "Japanese Yen", code: "JPY", symbol: "JP¥" },
                  ]
                    .filter(currency => availableCurrencies.includes(currency.code))
                    .map((currency, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between py-4 border-b border-light-grey last:border-b-0 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleCurrencySelect(currency.code)}
                      >
                        <div>
                          <div className="text-black">{currency.label}</div>
                          <div className="text-sm text-gray-500">
                            {currency.code} {currency.symbol}
                          </div>
                        </div>
                        {selectedCurrency === currency.code && (
                          <IconCheck width={24} height={24} />
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        );

      case "profile":
        return (
          <div className="w-full h-full bg-white flex flex-col">
            <div className="flex items-center justify-between px-4 py-6">
              <button onClick={handleBackToMain} className="text-2xl font-light">
                <IconChevronLeft width={12} height={20} />
              </button>
              <button onClick={handleMobileMenuClose} className="text-xl font-light">
                <IconClear width={15} height={15} />
              </button>
            </div>
            <div className="flex-1 px-4 flex flex-col overflow-y-auto">
              <MobileProfileForm onClose={handleMobileMenuClose} />
            </div>
          </div>
        );

      default:
        return (
          <div className="w-full h-full bg-white flex flex-col">
            <div className="flex items-center justify-between px-4 py-6">
              <Image
                src="/images/logo.png"
                className="w-[42px] h-[30px]"
                width={42}
                height={30}
                alt="Logo"
                priority
              />
              <button onClick={handleMobileMenuClose} className="text-xl font-light">
                <IconClear width={15} height={15} />
              </button>
            </div>
            <div className="flex-1">
              <div className="flex flex-col">
                {isAuthenticated ? (
                  <button
                    onClick={() => handleNavigateToView("profile")}
                    className="flex items-center justify-between px-4 mx-4 my-1 py-6 border-b border-black text-left hover:bg-gray-50"
                  >
                    <span>Profile</span>
                    <IconChevronRight width={8} height={14} fill="#1c1c1c" />
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setAuthView(FORM_TYPES.LOGIN);
                      handleNavigateToView("auth");
                    }}
                    className="flex items-center justify-between px-4 mx-4 my-1 py-6 border-b border-black text-left hover:bg-gray-50"
                  >
                    <span>Login / Register</span>
                    <IconChevronRight width={8} height={14} fill="#1c1c1c" />
                  </button>
                )}
                <Link
                  href="/wishlist"
                  onClick={handleMobileMenuClose}
                  className="flex items-center justify-between px-4 mx-4 my-1 py-6 border-b border-black text-left hover:bg-gray-50"
                >
                  <span>Wishlist</span>
                  <IconChevronRight width={8} height={14} fill="#1c1c1c" />
                </Link>
                <button
                  onClick={() => handleNavigateToView("currency")}
                  className="flex items-center justify-between px-4 mx-4 my-1 py-4 border-b border-black text-left hover:bg-gray-50"
                >
                  <div className="flex flex-col items-start">
                    <span>Currency</span>
                    <span className="text-sm text-gray-500 mt-1">
                      {selectedCurrency} {getCurrencySymbol(selectedCurrency)}
                    </span>
                  </div>
                  <IconChevronRight width={8} height={14} fill="#1c1c1c" />
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  const renderDesktopLangCurrencyContent = () => {
    return (
      <div className="w-[440px] bg-white border border-light-grey shadow-lg p-4">
        <div className="flex justify-end">
          <button
            className="bg-transparent border-none p-2 cursor-pointer"
            aria-label={searchOpen ? "Close search" : "Open search"}
            onClick={() => setLangCurrencyDropdownOpen(false)}
          >
            <IconClear width={15} height={15} />
          </button>
        </div>
        <div className="flex border-b border-light-grey font-raleway text-md mb-6">
          <button className="px-2 pb-3 text-center border-b-2 border-black">
            Currency
          </button>
        </div>
        <div>
          <p className="text-sm text-gray-600 mb-4">
            Choose the currency you want to see while browsing:
          </p>
          <div className="overflow-y-auto">
            <div className="space-y-0">
              {[
                { label: "US Dollar", code: "USD", symbol: "$" },
                { label: "Australian Dollar", code: "AUD", symbol: "A$" },
                { label: "Canadian Dollar", code: "CAD", symbol: "C$" },
                { label: "British Pound", code: "GBP", symbol: "£" },
                { label: "Euro", code: "EUR", symbol: "€" },
                { label: "Swiss Franc", code: "CHF", symbol: "CHF" },
                { label: "Hong Kong Dollar", code: "HKD", symbol: "HK$" },
                { label: "Chinese Yuan", code: "CNY", symbol: "CN¥" },
                { label: "Japanese Yen", code: "JPY", symbol: "JP¥" },
              ]
                .filter(currency => availableCurrencies.includes(currency.code))
                .map((currency, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 border-b border-light-grey last:border-b-0 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleCurrencySelect(currency.code)}
                  >
                    <div>
                      <div className="text-black">{currency.label}</div>
                      <div className="text-sm text-gray-500">
                        {currency.code} {currency.symbol}
                      </div>
                    </div>
                    {selectedCurrency === currency.code && <IconCheck width={20} height={20} />}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <header ref={ref} className="w-full bg-white py-5 px-3 fixed top-0 left-0 right-0 z-50 md:relative md:z-auto transition-border duration-200 border-b-0 md:border-b-0">
      <nav className="flex items-center gap-3">
        <div className="flex items-center">
          <Link href="/">
            <Image
              src="/images/logo.png"
              className="block w-[42px] h-[30px] md:w-[56px] md:h-[40px]"
              width={56}
              height={40}
              alt="Logo"
              priority
            />
          </Link>
        </div>
        <div className="flex flex-1 justify-end md:hidden">
          <div className="flex-1 mr-2 min-[320px]:block hidden">
            <ListingsSearch />
          </div>
          <button
            className="bg-transparent border-none p-2 cursor-pointer max-[319px]:block min-[320px]:hidden"
            aria-label={searchOpen ? "Close search" : "Open search"}
            onClick={handleSearchToggle}
            ref={searchBtnRef}
          >
            {searchOpen ? (
              <IconClear width={15} height={15} />
            ) : (
              <IconSearch width={24} height={24} />
            )}
          </button>
          <button
            className="bg-transparent border-none cursor-pointer relative"
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            onClick={handleMenuToggle}
            ref={menuBtnRef}
          >
            <Image
              src="/icons/hamburger.svg"
              alt="Menu"
              width={24}
              height={24}
              className="w-6 h-6"
            />
          </button>
        </div>
        <div className="hidden md:flex items-center flex-1 max-w-md min-w-[200px]">
          <ListingsSearch />
        </div>
        <div className="hidden md:flex flex-grow items-center justify-end gap-6">
          <div className="relative">
            <button
              ref={langCurrencyBtnRef}
              onClick={() => setLangCurrencyDropdownOpen(!langCurrencyDropdownOpen)}
              className="py-3 px-2 font-raleway text-sm text-black tracking-[0.1em] transition-colors hover:bg-light-grey"
            >
              ENG / {getCurrencySymbol(selectedCurrency)}
            </button>
            <Dropdown
              open={langCurrencyDropdownOpen}
              onClose={() => setLangCurrencyDropdownOpen(false)}
              className="absolute right-0 top-full mt-1 z-50"
              toggleRef={langCurrencyBtnRef}
            >
              {renderDesktopLangCurrencyContent()}
            </Dropdown>
          </div>
          <Link
            href="/wishlist"
            className="py-3 px-2 font-raleway text-sm text-black font-normal tracking-[0.1em] transition-colors hover:bg-light-grey"
          >
            WISHLIST
          </Link>
          {isLoading ? (
            <div className="py-3 px-2 font-raleway text-sm text-black font-normal tracking-[0.02em]">
              LOGIN
            </div>
          ) : isAuthenticated ? (
            <div className="relative">
              <button
                onClick={handleProfileClick}
                className="py-3 px-2 font-raleway text-sm text-black tracking-[0.1em] transition-colors hover:bg-light-grey"
              >
                {user?.first_name ? `${user?.first_name.toUpperCase()}` : "PROFILE"}
              </button>
            </div>
          ) : (
            <button
              onClick={handleLoginClick}
              className="py-3 px-2 font-raleway text-sm text-black tracking-[0.1em] transition-colors hover:bg-light-grey"
            >
              LOGIN
            </button>
          )}
        </div>
        <Dropdown
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          className="md:hidden max-[320px]:block min-[321px]:hidden absolute left-0 right-0 top-16 bg-white shadow-lg z-30"
          toggleRef={searchBtnRef}
        >
          <div className="p-3">
            <ListingsSearch />
          </div>
        </Dropdown>

        <div
          className={`
          fixed inset-0 bg-white z-50 md:hidden transform transition-transform duration-300 ease-in-out overflow-hidden
          ${menuOpen ? "translate-x-0" : "translate-x-full"}
        `}
        >
          <div className="h-full overflow-y-auto">
            {renderMobileNavigationContent()}
          </div>
        </div>
      </nav>
    </header>
  );
});

export default Header;
